import type { Json } from "@/integrations/supabase/types";
import { sanitizeProviderPublicText } from "./providerPublicText";

export type OperatingSystemStatus =
  | "not_created"
  | "queued"
  | "creating"
  | "ready"
  | "updating"
  | "failed"
  | "update_failed"
  | "auth_blocked"
  | "update_auth_blocked"
  | "archived";

export type OperatingSystemJobStatus =
  | "queued"
  | "running"
  | "waiting_external_api"
  | "blocked_auth"
  | "completed"
  | "failed"
  | "cancelled";

export interface OperatingSystemApp {
  id: string;
  user_id: string;
  name: string;
  status: OperatingSystemStatus;
  source_conversation_id: string;
  source_message_id: string | null;
  last_ready_source_message_id: string | null;
  latest_job_id: string | null;
  base44_app_id?: string | null;
  base44_app_url?: string | null;
  base44_embed_url?: string | null;
  base44_provider_status?: string | null;
  base44_reference?: Json;
  latest_job_feedback: string | null;
  failure_reason: string | null;
  version: number;
  created_at: string;
  updated_at: string;
  ready_at: string | null;
  archived_at: string | null;
  public_access_level?: "none" | "reader" | "editor" | null;
  properties?: unknown;
}

export const OPERATING_SYSTEM_DESCRIPTION_MAX_LENGTH = 1000;

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function debugCloneValidation(app: Pick<OperatingSystemApp, "properties"> | null): Record<string, unknown> | null {
  const properties = isRecord(app?.properties) ? app.properties : null;
  return isRecord(properties?.debug_clone_runtime_validation)
    ? properties.debug_clone_runtime_validation : null;
}

export function isDebugCloneRuntimePending(app: Pick<OperatingSystemApp, "properties"> | null): boolean {
  const validation = debugCloneValidation(app);
  return !!validation?.job_id && !validation.validated_at;
}

export function isDebugCloneRuntimeValidated(app: Pick<OperatingSystemApp, "properties"> | null): boolean {
  return !!debugCloneValidation(app)?.validated_at;
}

export function normalizeOperatingSystemDescription(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const description = value.replace(/\s+/g, " ").trim();
  return description ? description.slice(0, OPERATING_SYSTEM_DESCRIPTION_MAX_LENGTH) : null;
}

export function getOperatingSystemDescription(
  app: Pick<OperatingSystemApp, "properties"> | null | undefined,
): string | null {
  return normalizeOperatingSystemDescription(isRecord(app?.properties) ? app.properties.description : null);
}

export interface OperatingSystemRuntimeCache {
  operating_system_id: string;
  user_id: string;
  manifest?: Json;
  screenshot_url: string | null;
  embed_url?: string | null;
  direct_url?: string | null;
  last_ready_state: string | null;
  last_ready_version: number | null;
  last_ready_at: string | null;
  runtime_health: Json;
  created_at: string;
  updated_at: string;
}

export type OperatingSystemRuntimeLifecycleState =
  | "provider_loading"
  | "provider_auth_or_quota_blocked"
  | "source_unavailable"
  | "fallback_only_route"
  | "runtime_candidate_validated"
  | "runtime_loaded"
  | "runtime_error_after_load"
  | "failed";

export type OperatingSystemRuntimeAvailability =
  | "none"
  | "last_good_runtime_available"
  | "candidate_runtime_available"
  | "active_runtime_loaded";

export interface OperatingSystemRuntimeLifecycle {
  operating_system_id: string;
  user_id: string;
  job_id: string | null;
  lifecycle_state: OperatingSystemRuntimeLifecycleState;
  runtime_availability: OperatingSystemRuntimeAvailability;
  runtime_source: string | null;
  runtime_version: number | null;
  cache_key: string | null;
  reason: string | null;
  last_transition_source: string;
  last_transition_at: string;
  last_good_runtime_at: string | null;
  last_runtime_loaded_at: string | null;
  last_candidate_validated_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface UserOperatingSystemRuntimeState {
  user_id: string;
  last_opened_operating_system_id: string | null;
  last_opened_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface OperatingSystemJob {
  id: string;
  operating_system_id: string;
  user_id: string;
  job_type: "create" | "update" | "template_export" | "template_instance" | "template_retire";
  status: OperatingSystemJobStatus;
  progress_percent: number;
  current_step: string;
  input_json?: Json;
  partial_result_json?: Json;
  result_json?: Json;
  error_message: string | null;
  external_job_id?: string | null;
  provider_status?: string | null;
  idempotency_key: string;
  source_conversation_id: string;
  source_message_id: string | null;
  locked_until: string | null;
  attempt_count: number;
  max_attempts: number;
  next_run_at: string;
  created_at: string;
  updated_at: string;
  started_at: string | null;
  completed_at: string | null;
  failed_at: string | null;
}

export interface OperatingSystemJobEvent {
  id: string;
  operating_system_id: string;
  job_id: string | null;
  user_id: string;
  source: "applyt" | "base44_mcp" | "base44_editor" | "base44_usage" | "processor";
  event_type: string;
  role: string | null;
  content: string | null;
  public_metadata?: Json;
  raw_json?: Json;
  dedupe_key?: string;
  observed_at: string;
  created_at: string;
}

export interface ArtifactMessageForOs {
  id: string;
  content: string;
  is_user: boolean;
  created_at: string;
  kind?: "CHAT" | "ARTF" | "QUESTION" | "ANSWER" | "EDIT";
  parent_message_id?: string | null;
}

export interface OperatingSystemPreviewDescriptor {
  cacheKey: string;
  osId: string;
  name: string;
  url: string;
  embedUrl: string | null;
  directUrl: string | null;
  proxyExpiresAt: string | null;
  version: number;
  screenshotUrl: string | null;
  runtimeSource: "cache" | "app";
  hasCachedLastReadyRuntime: boolean;
  canEmbed: boolean;
  isDirectOnly: boolean;
}

export interface OperatingSystemProxyTicket {
  proxyUrl: string;
  expiresAt: string;
}

export const OPERATING_SYSTEM_APP_SAFE_SELECT = [
  "id",
  "user_id",
  "name",
  "status",
  "source_conversation_id",
  "source_message_id",
  "last_ready_source_message_id",
  "latest_job_id",
  "latest_job_feedback",
  "failure_reason",
  "version",
  "created_at",
  "updated_at",
  "ready_at",
  "archived_at",
  "public_access_level",
].join(",");

export const OPERATING_SYSTEM_RUNTIME_CACHE_SAFE_SELECT = [
  "operating_system_id",
  "user_id",
  "screenshot_url",
  "last_ready_state",
  "last_ready_version",
  "last_ready_at",
  "runtime_health",
  "created_at",
  "updated_at",
].join(",");

export const OPERATING_SYSTEM_RUNTIME_LIFECYCLE_SAFE_SELECT = [
  "operating_system_id",
  "user_id",
  "job_id",
  "lifecycle_state",
  "runtime_availability",
  "runtime_source",
  "runtime_version",
  "cache_key",
  "reason",
  "last_transition_source",
  "last_transition_at",
  "last_good_runtime_at",
  "last_runtime_loaded_at",
  "last_candidate_validated_at",
  "created_at",
  "updated_at",
].join(",");

export const OPERATING_SYSTEM_JOB_SAFE_SELECT = [
  "id",
  "operating_system_id",
  "user_id",
  "job_type",
  "status",
  "progress_percent",
  "current_step",
  "provider_status",
  "error_message",
  "source_conversation_id",
  "source_message_id",
  "locked_until",
  "attempt_count",
  "max_attempts",
  "next_run_at",
  "created_at",
  "updated_at",
  "started_at",
  "completed_at",
  "failed_at",
].join(",");

export const OPERATING_SYSTEM_JOB_EVENT_SAFE_SELECT = [
  "id",
  "operating_system_id",
  "job_id",
  "user_id",
  "source",
  "event_type",
  "role",
  "content",
  "public_metadata",
  "observed_at",
  "created_at",
].join(",");

export const OPERATING_SYSTEM_VISIBLE_HISTORY_FILTER = "source.neq.base44_editor,source.eq.base44_editor";

export const USER_OPERATING_SYSTEM_RUNTIME_STATE_SAFE_SELECT = [
  "user_id",
  "last_opened_operating_system_id",
  "last_opened_at",
  "created_at",
  "updated_at",
].join(",");

export const OPERATING_SYSTEM_SCREENSHOT_BUCKET = "operating-system-screenshots";
export const OPERATING_SYSTEM_SCREENSHOT_STORAGE_PREFIX = `storage://${OPERATING_SYSTEM_SCREENSHOT_BUCKET}/`;

export const ACTIVE_OS_STATUSES: OperatingSystemStatus[] = ["queued", "creating", "updating"];
export const ACTIVE_OS_JOB_STATUSES: OperatingSystemJobStatus[] = ["queued", "running", "waiting_external_api"];

const OS_PREVIEW_SNAPSHOT_STORAGE_KEY = "applyt.osPreviewSnapshots.v1";
const MAX_STORED_OS_PREVIEW_SNAPSHOTS = 24;
const OS_PREVIEW_RUNTIME_FRAME_CACHE_VERSION = "runtime-layout-v3";

export function getOperatingSystemDetailPath(osId: string): string {
  return `/applications/${encodeURIComponent(osId)}`;
}

export function getOperatingSystemRunPath(osId: string): string {
  return `${getOperatingSystemDetailPath(osId)}/run`;
}

export function getOperatingSystemSourcePath(
  app: Pick<OperatingSystemApp, "source_conversation_id" | "source_message_id">,
  destination: "chat" | "conversations" = "chat",
): string {
  if (destination === "chat") {
    return `/chat/${encodeURIComponent(app.source_conversation_id)}?from=applications`;
  }

  const params = new URLSearchParams({ conversationId: app.source_conversation_id });
  if (app.source_message_id) {
    params.set("messageId", app.source_message_id);
  }
  params.set("from", "applications");
  return `/conversations?${params.toString()}`;
}

export function openOperatingSystemRunInNewTab(osId: string): boolean {
  if (typeof window === "undefined") return false;
  const opened = window.open(getOperatingSystemRunPath(osId), "_blank", "noopener,noreferrer");
  if (opened) {
    opened.opener = null;
  }
  return !!opened;
}

export function isActiveOsStatus(status?: string | null): boolean {
  return !!status && ACTIVE_OS_STATUSES.includes(status as OperatingSystemStatus);
}

export function isActiveOsJobStatus(status?: string | null): boolean {
  return !!status && ACTIVE_OS_JOB_STATUSES.includes(status as OperatingSystemJobStatus);
}

export function isSupersededOperatingSystemUpdate(
  app: Pick<OperatingSystemApp, "status" | "latest_job_id"> | null | undefined,
  job: Pick<OperatingSystemJob, "id" | "status" | "error_message"> | null | undefined,
): boolean {
  return app?.status === "updating" &&
    job?.id === app.latest_job_id &&
    job.status === "cancelled" &&
    job.error_message === "Cancelled because a newer OS job superseded this job.";
}

export type OperatingSystemRestartOperation = "create" | "update";

export function getOperatingSystemRestartOperation(
  app: Pick<OperatingSystemApp, "status" | "base44_app_url" | "base44_embed_url" | "ready_at" | "version">,
): OperatingSystemRestartOperation {
  const hasReadyRuntime = !!(
    app.base44_app_url ||
    app.base44_embed_url ||
    app.ready_at ||
    (app.version ?? 0) > 0
  );

  return ["updating", "update_failed", "update_auth_blocked"].includes(app.status) && hasReadyRuntime ? "update" : "create";
}

export function hasOperatingSystemRunnableRuntime(
  app: Pick<OperatingSystemApp, "status" | "ready_at" | "version"> & Pick<OperatingSystemApp, "properties"> | null,
  runtimeCache: Pick<OperatingSystemRuntimeCache, "last_ready_at"> | null = null,
  lifecycle: Pick<OperatingSystemRuntimeLifecycle, "runtime_availability"> & Partial<Pick<OperatingSystemRuntimeLifecycle, "lifecycle_state">> | null = null,
): boolean {
  if (!app || app.status === "archived" || isDebugCloneRuntimePending(app)) return false;
  // A cold preview can record availability=none before a completed App is
  // opened again. Ticket issuance is what refreshes and wakes that runtime;
  // suppressing it here leaves the viewer permanently on its Ready placeholder.
  if (lifecycle?.lifecycle_state === "provider_loading" &&
    (runtimeCache?.last_ready_at || app.ready_at || (app.version ?? 0) > 0)) return true;
  if (lifecycle?.runtime_availability) return [
    "candidate_runtime_available",
    "last_good_runtime_available",
    "active_runtime_loaded",
  ].includes(lifecycle.runtime_availability);
  if (runtimeCache?.last_ready_at) return true;
  return app.status === "ready" || app.status === "update_auth_blocked" || !!app.ready_at || (app.version ?? 0) > 0;
}

export function isOperatingSystemRuntimeRevisionPending(
  app: Pick<OperatingSystemApp, "status" | "ready_at" | "version"> | null,
  runtimeCache: Pick<OperatingSystemRuntimeCache, "last_ready_at" | "last_ready_version"> | null = null,
): boolean {
  if (!app || app.status !== "ready") return false;
  const appVersion = Number.isSafeInteger(app.version) && (app.version ?? 0) > 0
    ? app.version as number
    : null;
  if (appVersion === null) return false;
  if (!runtimeCache?.last_ready_at) return true;

  const cacheVersion = Number.isSafeInteger(runtimeCache.last_ready_version)
    ? runtimeCache.last_ready_version as number
    : null;
  if (cacheVersion !== null) return cacheVersion < appVersion;

  const appReadyAt = app.ready_at ? Date.parse(app.ready_at) : Number.NaN;
  const cacheReadyAt = Date.parse(runtimeCache.last_ready_at);
  return Number.isFinite(appReadyAt) && Number.isFinite(cacheReadyAt) && cacheReadyAt < appReadyAt;
}

export function hasOperatingSystemEditableRuntime(
  app: Pick<OperatingSystemApp, "ready_at" | "version" | "status"> | null,
): boolean {
  if (!app) return false;
  if (app.status === "ready" || app.status === "update_failed") {
    return !!app.ready_at || (app.version ?? 0) > 0;
  }
  return false;
}

export function getOperatingSystemRestartLabel(
  app: Pick<OperatingSystemApp, "status" | "base44_app_url" | "base44_embed_url" | "ready_at" | "version">,
): string {
  if (getOperatingSystemRestartOperation(app) !== "update") return "Restart build";
  return app.status === "updating" ? "Restart update" : "Retry update";
}

export function isSupportManagedAppRecovery(job: Pick<OperatingSystemJob, "current_step"> &
  Partial<Pick<OperatingSystemJob, "status" | "error_message" | "partial_result_json">> | null | undefined): boolean {
  const partial = job?.partial_result_json;
  const saved = partial && typeof partial === "object" && !Array.isArray(partial)
    ? partial as Record<string, unknown> : {};
  const abandoned = saved.parked_update_recovery;
  if (job?.status === "failed" && job.current_step === "abandoned_unverified_update" &&
    abandoned && typeof abandoned === "object" && !Array.isArray(abandoned) &&
    (abandoned as Record<string, unknown>).state === "abandoned") return false;
  const settlement = saved.provider_usage_settlement;
  const currentSettlement = settlement && typeof settlement === "object" && !Array.isArray(settlement)
    ? (settlement as Record<string, unknown>).id : null;
  const repair = saved.backend_compatibility_repair;
  const repairAttempts = repair && typeof repair === "object" && !Array.isArray(repair)
    ? (repair as Record<string, unknown>).attempts : null;
  const historicalVerification = saved.historical_update_verification;
  const historicalState = historicalVerification && typeof historicalVerification === "object" && !Array.isArray(historicalVerification)
    ? (historicalVerification as Record<string, unknown>).state : null;
  if (job?.status === "failed" &&
    (historicalState === "checking" || historicalState === "review_required")) return true;
  if (job?.status === "failed" && saved.verification_only_recovery === true) return true;
  if (job?.status === "failed" && job.error_message === "App backend compatibility could not be repaired safely." &&
    repairAttempts === 1 && typeof currentSettlement === "string" && currentSettlement.length > 0) return true;
  return job?.current_step === "app_recovery_review" || job?.current_step === "provider_send_uncertain" ||
    job?.current_step === "usage_settlement_exhausted" || job?.current_step === "usage_debit_uncertain" ||
    job?.current_step === "unsafe_recovery_exhausted";
}

export type UserAppPrimaryStatus = "preparing" | "ready" | "action_needed" | "unavailable";
export type UserAppActivity = "building" | "updating" | null;
export type UserAppAction = "retry" | "continue_setup" | null;

export type UserAppState = {
  status: UserAppPrimaryStatus;
  label: "Preparing" | "Ready" | "Action needed" | "Could not finish App" | "Unavailable";
  activity: UserAppActivity;
  activityLabel: string | null;
  canOpen: boolean;
  showProgress: boolean;
  message: string | null;
  action: UserAppAction;
};

export type AppStatusDescriptor = {
  label: string;
  color: "neutral" | "blue" | "green" | "amber" | "red";
  badgeClass: string;
  availabilityMessage: string | null;
  activityLabel: string | null;
  userStatus: UserAppPrimaryStatus;
};

type RuntimeLifecycleSignal = Pick<OperatingSystemRuntimeLifecycle, "runtime_availability"> &
  Partial<Pick<OperatingSystemRuntimeLifecycle, "lifecycle_state">>;

type RuntimeLifecycleFeedbackSignal = RuntimeLifecycleSignal &
  Partial<Pick<OperatingSystemRuntimeLifecycle, "reason">>;

const APP_STATUS_CLASSES = {
  neutral: "border-border bg-muted text-muted-foreground",
  blue: "border-blue-200 bg-blue-50 text-blue-800",
  green: "border-emerald-200 bg-emerald-50 text-emerald-700",
  amber: "border-amber-300 bg-amber-50 text-amber-800",
  red: "border-destructive/30 bg-destructive/10 text-destructive",
};

function hasUserUsableRuntime(
  app: Pick<OperatingSystemApp, "status"> & Partial<Pick<OperatingSystemApp, "ready_at" | "version">>,
  lifecycle?: Pick<OperatingSystemRuntimeLifecycle, "runtime_availability"> | null,
  runtimeCache?: Pick<OperatingSystemRuntimeCache, "last_ready_at"> | null,
): boolean {
  if (
    lifecycle?.runtime_availability === "last_good_runtime_available" ||
    lifecycle?.runtime_availability === "active_runtime_loaded"
  ) {
    return true;
  }
  if (runtimeCache?.last_ready_at) return true;
  if (!!app.ready_at && (app.version ?? 0) > 0) return true;

  // These durable App statuses only exist after a usable version has been
  // promoted. Keep that user contract even when older rows do not include
  // complete runtime evidence in the current projection.
  return ["ready", "updating", "update_failed", "update_auth_blocked"].includes(app.status);
}

/**
 * Canonical projection from internal App/job/runtime state to the state a user
 * actually needs. Internal diagnostics stay intact; user UI consumes this
 * projection instead of exposing infrastructure transitions directly.
 */
export function getUserAppState({ app, job, lifecycle, runtimeCache }: {
  app: Pick<OperatingSystemApp, "status"> & Partial<Pick<OperatingSystemApp, "latest_job_id" | "ready_at" | "version" | "base44_provider_status">>;
  job?: Pick<OperatingSystemJob, "id" | "job_type" | "status" | "current_step"> &
    Partial<Pick<OperatingSystemJob, "provider_status" | "next_run_at">> | null;
  lifecycle?: RuntimeLifecycleSignal | null;
  runtimeCache?: Pick<OperatingSystemRuntimeCache, "last_ready_at"> | null;
}): UserAppState {
  const latest = job && (!app.latest_job_id || job.id === app.latest_job_id) ? job : null;
  const canOpen = hasUserUsableRuntime(app, lifecycle, runtimeCache);
  const unsafeRetry = isSupportManagedAppRecovery(latest);
  const nextRunAt = latest?.next_run_at ? Date.parse(latest.next_run_at) : Number.NaN;
  const scheduledWait = latest?.status === "waiting_external_api" && Number.isFinite(nextRunAt) &&
    nextRunAt <= Date.now() + 2 * 60 * 60_000;
  const reconcilingUsage = latest?.provider_status === "waiting_for_usage_settlement" && scheduledWait;
  const unscheduledUsage = latest?.provider_status === "waiting_for_usage_settlement" && !scheduledWait;
  const activeLatestJob = !!latest && isActiveOsJobStatus(latest.status) &&
    (latest.status !== "waiting_external_api" || scheduledWait || !latest.next_run_at);
  const updating = canOpen && !unsafeRetry && !unscheduledUsage && (
    app.status === "updating" ||
    (activeLatestJob && latest?.job_type === "update")
  );
  const updateFailed = canOpen && (
    app.status === "update_failed" ||
    (latest?.job_type === "update" && latest.status === "failed")
  );

  // Legacy holds and exhausted settlements cannot claim an active worker.
  if ((unsafeRetry || unscheduledUsage) && !canOpen) {
    return {
      status: "unavailable",
      label: "Could not finish App",
      activity: null,
      activityLabel: null,
      canOpen,
      showProgress: false,
      message: latest?.current_step === "usage_settlement_exhausted"
        ? "This build could not be completed safely. No debit was attempted for its unverified Provider usage. You can create a new App from a new Artifact."
        : "This build could not be completed safely. The prior charge or changes remain uncertain.",
      action: null,
    };
  }

  // A usable App remains Ready even while Applyt works on a replacement or
  // recovers from an update problem. This is the last-known-good rule in UI.
  if (canOpen) {
    return {
      status: "ready",
      label: "Ready",
      activity: updating ? "updating" : null,
      activityLabel: updating ? "Updating in background" : null,
      canOpen: true,
      showProgress: false,
      message: latest?.status === "failed" && latest.current_step === "abandoned_unverified_update"
        ? "Previous change could not be confirmed. You can continue working."
        : unsafeRetry || unscheduledUsage
        ? "Latest changes could not be completed safely. The previous App version is available."
        : reconcilingUsage
        ? "Recovering automatically"
        : updating
        ? "Updating in background"
        : updateFailed
          ? app.base44_provider_status === "runtime_recovered"
            ? "The App is available. The latest update still needs review."
            : "Latest changes weren't applied."
          : null,
      action: updateFailed && !unsafeRetry && !unscheduledUsage ? "retry" : null,
    };
  }

  const actionBlocked = app.status === "auth_blocked" ||
    latest?.status === "blocked_auth" ||
    lifecycle?.lifecycle_state === "provider_auth_or_quota_blocked";
  if (actionBlocked) {
    return {
      status: "action_needed",
      label: "Action needed",
      activity: null,
      activityLabel: null,
      canOpen: false,
      showProgress: false,
      message: "Setup not finished.",
      action: "continue_setup",
    };
  }

  const automaticallyWorking = app.status === "queued" ||
    app.status === "creating" ||
    activeLatestJob ||
    lifecycle?.lifecycle_state === "provider_loading" ||
    lifecycle?.lifecycle_state === "runtime_candidate_validated" ||
    lifecycle?.runtime_availability === "candidate_runtime_available";
  if (automaticallyWorking) {
    return {
      status: "preparing",
      label: "Preparing",
      activity: "building",
      activityLabel: null,
      canOpen: false,
      showProgress: true,
      message: reconcilingUsage ? "Recovering automatically" : "Finishing your App",
      action: null,
    };
  }

  const terminalFailure = app.status === "failed" ||
    app.status === "update_failed" ||
    latest?.status === "failed" ||
    lifecycle?.lifecycle_state === "failed" ||
    lifecycle?.lifecycle_state === "source_unavailable" ||
    lifecycle?.lifecycle_state === "fallback_only_route" ||
    lifecycle?.lifecycle_state === "runtime_error_after_load";
  if (terminalFailure) {
    return {
      status: "unavailable",
      label: "Could not finish App",
      activity: null,
      activityLabel: null,
      canOpen: false,
      showProgress: false,
      message: "Could not finish App.",
      action: "retry",
    };
  }

  return {
    status: "preparing",
    label: "Preparing",
    activity: "building",
    activityLabel: null,
    canOpen: false,
    showProgress: true,
    message: null,
    action: null,
  };
}

export function getAppStatusDescriptor({ app, job, lifecycle, runtimeCache }: {
  app: Pick<OperatingSystemApp, "status"> & Partial<Pick<OperatingSystemApp, "latest_job_id" | "ready_at" | "version" | "base44_provider_status">>;
  job?: Pick<OperatingSystemJob, "id" | "job_type" | "status" | "current_step"> | null;
  lifecycle?: RuntimeLifecycleSignal | null;
  runtimeCache?: Pick<OperatingSystemRuntimeCache, "last_ready_at"> | null;
}): AppStatusDescriptor {
  if (app.status === "archived") {
    return {
      label: "Archived",
      color: "neutral",
      badgeClass: APP_STATUS_CLASSES.neutral,
      availabilityMessage: null,
      activityLabel: null,
      userStatus: "unavailable",
    };
  }

  const state = getUserAppState({ app, job, lifecycle, runtimeCache });
  const color: AppStatusDescriptor["color"] = state.status === "ready"
    ? "green"
    : state.status === "preparing"
      ? "blue"
      : state.status === "action_needed"
        ? "amber"
        : "red";

  return {
    label: state.label,
    color,
    badgeClass: APP_STATUS_CLASSES[color],
    availabilityMessage: state.message,
    activityLabel: state.activityLabel,
    userStatus: state.status,
  };
}

/** Keep the Template instance label and its severity on the same projection. */
export function getTemplateInstanceStatusDescriptor(
  descriptor: AppStatusDescriptor,
  instanceStatus?: string | null,
): AppStatusDescriptor {
  if (instanceStatus === "failed") {
    return {
      ...descriptor,
      label: "Error",
      color: "red",
      badgeClass: APP_STATUS_CLASSES.red,
      availabilityMessage: descriptor.userStatus === "ready"
        ? "This App opens, but Template preparation reported an error. Retry preparation."
        : "Template preparation failed. Retry to create this App.",
    };
  }
  if (instanceStatus === "preparing") {
    return { ...descriptor, label: "Preparing", color: "blue", badgeClass: APP_STATUS_CLASSES.blue };
  }
  return descriptor;
}

export function getAppStatusFeedback(feedback: string | null, descriptor: AppStatusDescriptor): string | null {
  // User-facing App state is intentionally stable. Raw job/runtime feedback is
  // diagnostic data and must not make a healthy or self-recovering App look
  // broken. The descriptor already contains any message that needs user action.
  void feedback;
  if (descriptor.label === "Ready") {
    return descriptor.activityLabel || descriptor.availabilityMessage;
  }
  if (descriptor.label === "Preparing") {
    return descriptor.availabilityMessage || "Preparing your App…";
  }
  if (descriptor.label === "Action needed" || descriptor.label === "Unavailable" || descriptor.label === "Could not finish App") {
    return descriptor.availabilityMessage;
  }
  return null;
}

export function getOperatingSystemStatusLabel(status?: string | null): string {
  return getAppStatusDescriptor({ app: { status: (status ?? "not_created") as OperatingSystemStatus } }).label;
}

export function getOperatingSystemRuntimeLifecycleLabel(
  lifecycle?: RuntimeLifecycleSignal | null,
): string | null {
  if (!lifecycle) return null;
  if (
    lifecycle.runtime_availability === "last_good_runtime_available" ||
    lifecycle.runtime_availability === "active_runtime_loaded"
  ) return "Ready";

  switch (lifecycle.lifecycle_state) {
    case "provider_auth_or_quota_blocked":
      return "Action needed";
    case "source_unavailable":
    case "fallback_only_route":
    case "runtime_error_after_load":
    case "failed":
      return "Unavailable";
    case "provider_loading":
    case "runtime_candidate_validated":
      return "Preparing";
    case "runtime_loaded":
      return "Ready";
    default:
      return "Preparing";
  }
}

export function getOperatingSystemUserReadyStatusLabel(
  appStatus?: string | null,
  lifecycle?: RuntimeLifecycleSignal | null,
): string {
  return getAppStatusDescriptor({ app: { status: (appStatus ?? "not_created") as OperatingSystemStatus }, lifecycle }).label;
}

export function getOperatingSystemStepLabel(step?: string | null): string {
  switch (step) {
    case "preparing_template_preview":
      return "Preparing Template preview";
    case "ready":
      return "Ready";
    case "base44_auth_blocked":
      return "Action needed";
    case "failed":
      return "Unavailable";
    case "update_failed":
      return "Ready";
    case "starting_os_build":
    case "preparing_os_build":
    case "sending_artifact_to_base44":
    case "waiting_for_base44":
    case "waiting_for_base44_preview":
    case "waiting_for_provider_quota":
    case "checking_app_compatibility":
    case "repairing_app_compatibility":
      return "Preparing your App";
    default:
      return step ? "Preparing your App" : "Preparing your App";
  }
}

function isTransientInfrastructureFeedback(value: string): boolean {
  return [
    /connection\s+(?:interrupted|reset|error|refused|closed)/i,
    /network\s+(?:error|failure|timeout)/i,
    /temporarily\s+unavailable/i,
    /database system is not accepting connections/i,
    /\b57p03\b/i,
    /\bhttp\s*503\b/i,
    /\b503\b.*\bunavailable\b/i,
    /\b(?:provider|base44|supabase)\b.*\b(?:connection|preview|timeout|unavailable|retry)\b/i,
  ].some((pattern) => pattern.test(value));
}

function neutralizeInfrastructureTerms(value: string): string {
  return value
    .replace(/\bBase44\b/gi, "service")
    .replace(/\bSupabase\b/gi, "service")
    .replace(/\bProvider\b/gi, "service")
    .replace(/\bbuilder\b/gi, "service");
}

export function getOperatingSystemFeedbackLabel(feedback?: string | null): string | null {
  const value = feedback?.trim();
  if (!value) return null;
  // Older rows may contain an object coerced by a backend retry path. Do not
  // expose that implementation artifact while those rows age out.
  if (value === "[object Object]") return "Provider update unavailable.";

  switch (value) {
    case "Preparing App build":
    case "Sending artifact to Provider":
    case "Sending artifact to Base44":
    case "Waiting for Base44":
    case "Waiting for Provider":
    case "Waiting for Base44 preview to finish waking up":
    case "Waiting for Base44 preview to respond":
    case "Waiting for Base44 preview":
      return "Preparing your App";
    case "Paused: Base44 needs to be reconnected by an admin.":
      return "Setup not finished.";
    case "Base44 auth recovered; job resumed.":
      return "Preparing your App";
    default: {
      const sanitized = sanitizeProviderPublicText(value);
      if (!sanitized || isTransientInfrastructureFeedback(sanitized)) return null;
      return neutralizeInfrastructureTerms(sanitized);
    }
  }
}

export function getOperatingSystemRuntimeLifecycleFeedback(
  lifecycle?: RuntimeLifecycleFeedbackSignal | null,
): string | null {
  if (!lifecycle) return null;
  if (
    lifecycle.runtime_availability === "last_good_runtime_available" ||
    lifecycle.runtime_availability === "active_runtime_loaded" ||
    lifecycle.lifecycle_state === "runtime_loaded"
  ) return null;

  switch (lifecycle.lifecycle_state) {
    case "provider_auth_or_quota_blocked":
      return "Setup not finished.";
    case "source_unavailable":
    case "fallback_only_route":
    case "runtime_error_after_load":
    case "failed":
      return "We couldn't finish preparing this App.";
    case "provider_loading":
    case "runtime_candidate_validated":
    default:
      return "Preparing your App.";
  }
}

function getRecord(value: Json | null | undefined): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function getFirstString(record: Record<string, unknown> | null, keys: string[]): string | null {
  if (!record) return null;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

function normalizeUrl(value?: string | null): string | null {
  const url = value?.trim();
  return url || null;
}

export function getOperatingSystemScreenshotStoragePath(value?: string | null): string | null {
  const raw = value?.trim();
  if (!raw) return null;
  if (raw.startsWith(OPERATING_SYSTEM_SCREENSHOT_STORAGE_PREFIX)) {
    const path = raw.slice(OPERATING_SYSTEM_SCREENSHOT_STORAGE_PREFIX.length);
    return path || null;
  }

  try {
    const url = new URL(raw);
    const marker = [
      `/storage/v1/object/public/${OPERATING_SYSTEM_SCREENSHOT_BUCKET}/`,
      `/storage/v1/object/sign/${OPERATING_SYSTEM_SCREENSHOT_BUCKET}/`,
      `/storage/v1/object/${OPERATING_SYSTEM_SCREENSHOT_BUCKET}/`,
    ].find((candidate) => url.pathname.includes(candidate));
    if (!marker) return null;
    const markerIndex = url.pathname.indexOf(marker);
    const encodedPath = url.pathname.slice(markerIndex + marker.length);
    return encodedPath ? decodeURIComponent(encodedPath) : null;
  } catch {
    return null;
  }
}

export function isOperatingSystemScreenshotStorageReference(value?: string | null): boolean {
  return !!getOperatingSystemScreenshotStoragePath(value);
}

export function getOperatingSystemPreviewScreenshotUrl(app: OperatingSystemApp | null): string | null {
  const reference = getRecord(app?.base44_reference);
  return getFirstString(reference, [
    "screenshotUrl",
    "screenshot_url",
    "thumbnailUrl",
    "thumbnail_url",
    "previewScreenshotUrl",
    "preview_screenshot_url",
  ]);
}

export function getOperatingSystemPreviewDescriptor(
  app: OperatingSystemApp | null,
  runtimeCache: OperatingSystemRuntimeCache | null = null,
  proxyTicket: OperatingSystemProxyTicket | null = null,
): OperatingSystemPreviewDescriptor | null {
  if (!app || app.status === "archived" || isDebugCloneRuntimePending(app)) return null;
  const proxyUrl = normalizeUrl(proxyTicket?.proxyUrl);
  if (!proxyUrl) return null;
  const directUrl = getOperatingSystemRunPath(app.id);

  const hasCachedLastReadyRuntime = !!runtimeCache?.last_ready_at;
  const appRuntimeAllowed = app.status === "ready" || app.status === "update_auth_blocked";

  if (hasCachedLastReadyRuntime) {
    const version = Number.isFinite(runtimeCache?.last_ready_version)
      ? runtimeCache?.last_ready_version ?? 0
      : Number.isFinite(app.version)
        ? app.version
        : 0;
    return {
      cacheKey: `${app.id}:runtime:${version}:proxy:${OS_PREVIEW_RUNTIME_FRAME_CACHE_VERSION}`,
      osId: app.id,
      name: app.name || "App",
      url: proxyUrl,
      embedUrl: proxyUrl,
      directUrl,
      proxyExpiresAt: proxyTicket?.expiresAt ?? null,
      version,
      screenshotUrl: normalizeUrl(runtimeCache?.screenshot_url) || getOperatingSystemPreviewScreenshotUrl(app),
      runtimeSource: "cache",
      hasCachedLastReadyRuntime,
      canEmbed: true,
      isDirectOnly: false,
    };
  }

  if (!appRuntimeAllowed) return null;

  const version = Number.isFinite(app.version) ? app.version : 0;
  return {
    cacheKey: `${app.id}:runtime:${version}:proxy:${OS_PREVIEW_RUNTIME_FRAME_CACHE_VERSION}`,
    osId: app.id,
    name: app.name || "App",
    url: proxyUrl,
    embedUrl: proxyUrl,
    directUrl,
    proxyExpiresAt: proxyTicket?.expiresAt ?? null,
    version,
    screenshotUrl: getOperatingSystemPreviewScreenshotUrl(app),
    runtimeSource: "app",
    hasCachedLastReadyRuntime: false,
    canEmbed: true,
    isDirectOnly: false,
  };
}

function readPreviewSnapshotStore(): Record<string, { screenshotUrl: string; updatedAt: number }> {
  if (typeof window === "undefined") return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(OS_PREVIEW_SNAPSHOT_STORAGE_KEY) || "{}");
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const entries = Object.entries(parsed as Record<string, { screenshotUrl?: unknown; updatedAt?: unknown }>)
      .filter(([key, value]) => (
        !/base44/i.test(key) &&
        typeof value?.screenshotUrl === "string" &&
        !/https:\/\/[^/]*base44[^/\s]*/i.test(value.screenshotUrl) &&
        typeof value.updatedAt === "number"
      ))
      .map(([key, value]) => [key, {
        screenshotUrl: value.screenshotUrl as string,
        updatedAt: value.updatedAt as number,
      }]);
    const store = Object.fromEntries(entries);
    if (entries.length !== Object.keys(parsed).length) writePreviewSnapshotStore(store);
    return store;
  } catch {
    return {};
  }
}

function writePreviewSnapshotStore(store: Record<string, { screenshotUrl: string; updatedAt: number }>) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(OS_PREVIEW_SNAPSHOT_STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Snapshot caching is a best-effort speed improvement.
  }
}

export function rememberOperatingSystemPreviewSnapshot(descriptor: OperatingSystemPreviewDescriptor | null) {
  const screenshotUrl = descriptor?.screenshotUrl?.trim();
  if (!descriptor || !screenshotUrl) return;

  const store = readPreviewSnapshotStore();
  const nextStore = {
    ...store,
    [descriptor.cacheKey]: {
      screenshotUrl,
      updatedAt: Date.now(),
    },
  };

  const entries = Object.entries(nextStore)
    .sort(([, first], [, second]) => second.updatedAt - first.updatedAt)
    .slice(0, MAX_STORED_OS_PREVIEW_SNAPSHOTS);

  writePreviewSnapshotStore(Object.fromEntries(entries));
}

export function getStoredOperatingSystemPreviewSnapshot(descriptor: OperatingSystemPreviewDescriptor | null): string | null {
  if (!descriptor) return null;
  if (descriptor.screenshotUrl) return descriptor.screenshotUrl;
  const stored = readPreviewSnapshotStore()[descriptor.cacheKey]?.screenshotUrl;
  return typeof stored === "string" && stored.trim() ? stored : null;
}

export function extractArtifactTitle(markdown: string, fallback = "Untitled App"): string {
  const clean = (value: string) =>
    value
      .replace(/^#+\s*/, "")
      .replace(/[*_`>#-]/g, "")
      .replace(/\s+/g, " ")
      .trim();

  const heading = markdown
    .split("\n")
    .map((line) => line.trim())
    .find((line) => /^#{1,3}\s+\S/.test(line));

  const title = clean(heading || markdown.split("\n").find((line) => line.trim()) || "");
  return (title || fallback).slice(0, 140);
}

function getAncestorIds(messageId: string, byId: Map<string, ArtifactMessageForOs>): Set<string> {
  const ids = new Set<string>();
  let current = byId.get(messageId);
  let hops = 0;

  while (current && hops < 80) {
    ids.add(current.id);
    const parentId = current.parent_message_id || null;
    current = parentId ? byId.get(parentId) : undefined;
    hops += 1;
  }

  return ids;
}

function findAncestor(
  message: ArtifactMessageForOs,
  byId: Map<string, ArtifactMessageForOs>,
  predicate: (candidate: ArtifactMessageForOs) => boolean,
): ArtifactMessageForOs | null {
  let current: ArtifactMessageForOs | undefined = message;
  let hops = 0;

  while (current && hops < 80) {
    if (predicate(current)) return current;
    const parentId = current.parent_message_id || null;
    current = parentId ? byId.get(parentId) : undefined;
    hops += 1;
  }

  return null;
}

function getArtifactAnchorId(artifact: ArtifactMessageForOs, byId: Map<string, ArtifactMessageForOs>): string {
  let current: ArtifactMessageForOs | undefined = artifact;
  let hops = 0;

  while (current && hops < 80) {
    const parentId = current.parent_message_id || null;
    const parent = parentId ? byId.get(parentId) : null;

    if (current.is_user && current.kind === "ARTF") return current.id;

    if (current.is_user && current.kind === "ANSWER") {
      const question = findAncestor(current, byId, (candidate) => !candidate.is_user && candidate.kind === "QUESTION");
      return question?.id || current.id;
    }

    if (current.is_user && current.kind === "EDIT" && parent?.kind === "ARTF") {
      return getArtifactAnchorId(parent, byId);
    }

    current = parent || undefined;
    hops += 1;
  }

  return artifact.id;
}

export function areArtifactsRelated(
  first: ArtifactMessageForOs,
  second: ArtifactMessageForOs,
  messages: ArtifactMessageForOs[],
): boolean {
  if (first.id === second.id) return true;

  const byId = new Map(messages.map((message) => [message.id, message]));
  const firstAncestors = getAncestorIds(first.id, byId);
  const secondAncestors = getAncestorIds(second.id, byId);

  if (firstAncestors.has(second.id) || secondAncestors.has(first.id)) return true;

  return getArtifactAnchorId(first, byId) === getArtifactAnchorId(second, byId);
}

export function findOperatingSystemForArtifact({
  selectedArtifact,
  allArtifacts,
  messages,
  apps,
}: {
  selectedArtifact: ArtifactMessageForOs;
  allArtifacts: ArtifactMessageForOs[];
  messages: ArtifactMessageForOs[];
  apps: OperatingSystemApp[];
}): OperatingSystemApp | null {
  const direct = apps.find((app) => app.source_message_id === selectedArtifact.id);
  if (direct) return direct;

  const artifactById = new Map(allArtifacts.map((artifact) => [artifact.id, artifact]));
  const relatedApps = apps
    .map((app) => ({
      app,
      artifact: app.source_message_id ? artifactById.get(app.source_message_id) || null : null,
    }))
    .filter(({ artifact }) => artifact && areArtifactsRelated(selectedArtifact, artifact, messages))
    .sort((a, b) => new Date(b.app.updated_at).getTime() - new Date(a.app.updated_at).getTime());

  return relatedApps[0]?.app ?? null;
}

export function isArtifactNewerThanOsSource(
  selectedArtifact: ArtifactMessageForOs,
  app: OperatingSystemApp | null,
  allArtifacts: ArtifactMessageForOs[],
): boolean {
  if (!app || app.source_message_id === selectedArtifact.id) return false;
  if (!app.source_message_id) return false;
  const sourceArtifact = allArtifacts.find((artifact) => artifact.id === app.source_message_id);
  if (!sourceArtifact) return true;
  return new Date(selectedArtifact.created_at).getTime() > new Date(sourceArtifact.created_at).getTime();
}
